/**
 * PaymentLinkAction.tsx — mint the Stripe-hosted payment page for one order
 * case, email it to the patient, and hand the admin the URL to send themselves.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: patient recovery
 * (CTO 2026-09-04).
 *
 * WHY: a renewal capture can fail (card declined) and a month-1 pre-auth can
 * lapse, and the fix is always the same — get the patient in front of a payment
 * page so they can pay or put a working card on. Until now the back office
 * could SEE the failure on this board and had nothing to do about it;
 * orders-service has minted hosted links since G3 but nothing in the portal
 * called the route.
 *
 * ⚠️ ADMIN ONLY, AND THAT IS NOT A COPY-PASTE SLIP. The plan-link control in
 * `pipeline/PlanLinkActions.tsx` sits a few clicks away in the same product and
 * is deliberately CLINICIAN+ADMIN. The two controls, and the two rules:
 *
 *   PLAN link (CLINICIAN+ADMIN)   `/weightloss#resume=<assessmentId>` — the
 *       patient's own journey. No amount, no card, no Stripe. Whoever has the
 *       patient on the phone may send it.
 *   PAYMENT link (ADMIN, here)    a LIVE Stripe payment page charging the
 *       patient's own money. "Clinicians never see money" holds: the single
 *       carve-out in this estate is a clinician's OWN compensation, and patient
 *       money is not that. Do not widen this to CLINICIAN to make the two
 *       controls "consistent" — they are consistent, with the money rule.
 *
 * `@Roles('ADMIN')` on POST /v1/orders/:id/payment-link is the real enforcement
 * (a clinician session gets a 403 from the server whatever the portal renders).
 * This component is only ever mounted from the admin Payments section, which is
 * ADMIN-only end to end — the hiding here is affordance, not security.
 *
 * A 200 FROM THIS ROUTE MEANS A LINK, NEVER AN EMAIL. Mail faults answer 200
 * on purpose, because the body carries the minted URL and a 503 would throw
 * away the only copy of a live payment link. Every outcome therefore reads
 * `emailOutcome` and says plainly whether the patient has it — an admin told
 * "emailed" when nothing was sent closes the ticket and the patient stays stuck.
 */
import { useState } from 'react';
import { ApiError } from '../api/client';
import { createPaymentLink } from '../api/orders';
import type { PaymentLinkEmailOutcome, PaymentLinkResult } from '../api/types';
import { CopyLinkButton } from '../staff/ui';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Button, Help, type AlertTone } from '../ui/kit';

/**
 * What each outcome MEANS to the desk, and whether the copy affordance is the
 * point. `emailed` decides the wording ("emailed" vs "copy the link");
 * `retryable` decides whether offering another go is honest — a guest order
 * with no address on file will never send, however many times it is clicked.
 */
const OUTCOME_DISPLAY: Record<
  PaymentLinkEmailOutcome,
  { tone: AlertTone; text: string; retryable: boolean }
> = {
  sent: { tone: 'success', text: 'Link created and emailed to the patient.', retryable: false },
  already_sent: {
    // NOT a failure: the patient already holds this exact email. Nothing was
    // re-sent, and saying "failed" here would start a pointless chase.
    tone: 'success',
    text: 'Link created — already emailed. The patient has this email already, so nothing new was sent.',
    retryable: false,
  },
  no_recipient: {
    tone: 'warning',
    text: 'Link created — no email address on file, so nothing was sent. Copy the link and send it to the patient (this is the WhatsApp / SMS path).',
    retryable: false,
  },
  failed: {
    tone: 'danger',
    text: 'Link created — the email did NOT send. Copy the link and send it to the patient, or try the email again.',
    retryable: true,
  },
  stub: {
    tone: 'warning',
    text: 'Link created — email is not switched on in this environment, so nothing was transmitted. Copy the link instead.',
    retryable: false,
  },
};

/**
 * Absent or unrecognised `emailOutcome`. The field is a local orders widening
 * (not in contracts), so an older orders build omits it and a later one may add
 * a value this portal has never heard of. Both fall back to NOT-SENT with the
 * copy path foregrounded: the failure that matters is telling a desk the
 * patient was emailed when they were not.
 */
const OUTCOME_UNKNOWN = {
  tone: 'warning' as AlertTone,
  text: 'Link created — we cannot confirm the email went out. Copy the link and send it to the patient.',
  retryable: true,
};

export function PaymentLinkAction({ orderCaseId }: { orderCaseId: string }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PaymentLinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function mint() {
    // Idempotency makes a second click harmless server-side (same Stripe mint,
    // and the email answers `already_sent`) — this guard is UX only.
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // No `amountPence`: bill the order's OWN monthlyPence. The portal must
      // never invent a figure for a patient's card — the amount is the order's,
      // decided when the plan was bought. (The server 400s an order with no
      // monthly price, which is the honest outcome for a case that has no
      // recurring amount to charge.)
      const minted = await createPaymentLink(orderCaseId);
      setResult(minted);
      setConfirming(false);
    } catch (err) {
      // Close the dialog so the banner is not stranded behind the overlay —
      // the same reasoning the admin Refund flow already pins.
      setConfirming(false);
      setError(
        err instanceof ApiError && err.detail
          ? `We couldn't create the payment link — ${err.detail}`
          : 'Sorry — we could not create the payment link. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  const outcome = result ? (OUTCOME_DISPLAY[result.emailOutcome as PaymentLinkEmailOutcome] ?? OUTCOME_UNKNOWN) : null;

  /**
   * OFFER A RETRY ONLY WHERE ONE CAN WORK. `failed` (and the unknown
   * fallback) leaves a re-claimable claim row at notifications, so trying
   * again genuinely can send. `no_recipient` never can — there is no address
   * on file — and `sent` / `already_sent` would just be suppressed as
   * duplicates. A button that cannot succeed is worse than no button: it keeps
   * a desk clicking instead of picking up the phone, which is the whole
   * failure this lane exists to end.
   */
  const canSend = outcome === null || outcome.retryable;

  return (
    <div style={{ display: 'grid', gap: '4px', justifyItems: 'start' }}>
      {canSend ? (
        <Button type="button" variant="ghost" onClick={() => setConfirming(true)} disabled={busy} style={{ width: 'auto' }}>
          {busy ? 'Creating…' : result ? 'Try the email again' : 'Send payment link'}
        </Button>
      ) : null}

      {error ? (
        <Alert tone="danger" role="alert" style={{ marginBottom: 0 }}>
          {error}
        </Alert>
      ) : null}

      {result && outcome ? (
        <>
          <Alert tone={outcome.tone} role={outcome.tone === 'success' ? 'status' : 'alert'} style={{ marginBottom: 0 }}>
            {outcome.text}
          </Alert>
          {/* The URL itself is ALWAYS offered, whatever the email did — it is
              the whole reason the route answers 200 on a mail fault, and the
              only route to a patient with no address on file. */}
          <CopyLinkButton value={result.url} label="Copy payment link" variant="ghost" style={{ width: 'auto' }} />
          <Help style={{ margin: 0 }}>Payment page — the patient pays or updates their card on it.</Help>
        </>
      ) : null}

      {/* The estate's confirm grammar for an action that contacts a patient:
          who it reaches, what lands, and what does NOT change. No reason field
          — orders writes its own audit row, and the desk is mid-call. */}
      <ConfirmDialog
        open={confirming}
        title="Send the patient a payment link?"
        confirmLabel={busy ? 'Creating…' : 'Create and email the link'}
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void mint()}
        body={
          <>
            <p style={{ margin: '0 0 8px' }}>
              This creates a payment page for this order and emails the patient a link to it, at the address on
              their record. They can pay the outstanding month or put a new card on from that page.
            </p>
            <p style={{ margin: 0 }}>
              Nothing is charged now and no card is taken here &mdash; the patient does that themselves. You will
              get the link back either way, so you can send it over WhatsApp or SMS if the email cannot reach them.
            </p>
          </>
        }
      />
    </div>
  );
}
